import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, history } from 'umi';
import LiveVisualization from './pages/LiveVisualization';
import LiveVisualizationAll from './pages/LiveVisualizationAll';
import LiveFinance from './pages/LiveFinance';
import './index.scss';
import { getLiveTitleSummary } from '@sg/services';
import GameSelectAll from '@sg/components/GameSelectAll';
import { createBtnPoint, SGBuriedBtnPoint, LiveTitlesTab } from '@/pages/strategic/utils/buriedPoint';
import AccessRestricted from '@sg/components/AccessRestricted';
import {
  buildLiveTitlesUrl,
  parseLiveTitlesUrl,
} from '@sg/pages/StudioManagement/LiveTitles/utils/liveTitlesUrl';

export default function LiveTitle() {
  const location = useLocation();
  const { liveView, gameTitle, isLegacy } = useMemo(
    () => parseLiveTitlesUrl(location.search),
    [location.search],
  );
  const [hasProjectAccess, setHasProjectAccess] = useState<boolean>(true);

  // Canonicalize legacy bookmarks missing live_view once at entry.
  useEffect(() => {
    if (isLegacy) {
      history.replace(
        buildLiveTitlesUrl({ liveView: 'business', gameTitle }),
      );
    }
  }, [isLegacy, gameTitle]);

  // Studio list state for URL-based synchronization
  const [studioList, setStudioList] = useState<any[]>([]);
  // 当前选择的游戏
  const [studio, setStudio] = useState<{
    name: string;
    unified_id: string;
  }>({
    name: 'All',
    unified_id: 'all',
  });

  // Synchronize studio from parsed URL and studioList
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentSubtitle = searchParams.get('subtitle');

    // Only sync if we're on the "titles" subtitle
    if (currentSubtitle !== 'titles') {
      return;
    }

    if (!gameTitle || gameTitle === 'all') {
      // Initial entry (no studio_id) or explicit "all" → default to ALL page
      setStudio({
        name: 'All',
        unified_id: 'all',
      });
    } else if (studioList.length > 0) {
      const studioFromUrl = studioList.find(
        (s: any) => s.unified_id === gameTitle,
      );
      if (studioFromUrl) {
        setStudio({
          name: studioFromUrl.name,
          unified_id: studioFromUrl.unified_id,
        });
      } else {
        // URL studio does not exist in current list, fallback to ALL.
        setStudio({
          name: 'All',
          unified_id: 'all',
        });
      }
    }
  }, [location.search, studioList, gameTitle]);

  const fetchProjectData = async () => {
    const res = await getLiveTitleSummary({
      // depends on the default payload structure (when select all)
      "metrics": [
        "gross_revenue",
        "gross_base_sales",
        "gross_gaas_sales",
        "net_revenue",
        "net_base_sales",
        "net_gaas_sales",
        "unit_sales",
        "base_sales",
        "dlc_sales",
        "mau",
        "avg_dau",
        "pcu",
        "new_users",
        "returned_users",
        "retained_users",
        "payers",
        "payers_ratio",
        "arpu",
        "arppu"
      ],
      "os": ["All Systems"],
      "country_code": ["255"], // all selected
      "start_date": "2024-07-01",
      "end_date": "2025-07-31"
    });
    return (
      res?.list?.map((item: any, index: number) => ({
        name: item.game_title,
        cover: item.cover,
        // Use stable business key from API; only fallback to index when title is missing.
        unified_id: String(item.game_title || index + 1),
        originalData: item,
      })) || []
    );
  };

  const handleStudioChange = (studioItem: any) => {
    // Track buried point for studio selection
    const studioName = studioItem?.name || 'Unknown';
    const studioId = studioItem?.unified_id || '';
    const btnPoint = createBtnPoint(
      LiveTitlesTab,
      'studioSelect',
      `Studio Select - Studio: ${studioName}${studioId ? ` (ID: ${studioId})` : ''}`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    // URL update is now handled by GameSelectAll component via onBuildUrl prop
    // studio is synced from URL in the useEffect above
  };

  const handleStudioDataLoaded = (data: any[]) => {
    setStudioList(data);
    setHasProjectAccess(data && data.length > 0); // if no data meaning no access
    // GameSelectAll handles initial selection based on defaultUnifiedId prop
  };

  const handleBuildUrl = useCallback(
    (_loc: any, unifiedId: string) =>
      buildLiveTitlesUrl({ liveView: 'business', gameTitle: unifiedId }),
    [],
  );

  const urlUpdateCondition = useCallback((loc: any) => {
    const params = new URLSearchParams(loc.search);
    return params.get('subtitle') === 'titles';
  }, []);

  if (liveView === 'finance') {
    return (
      <div className="dbs-studio-live-title">
        <LiveFinance />
      </div>
    );
  }

  if (!hasProjectAccess) {
    // Show Access Restricted if no studios available after permission filtering
    return <AccessRestricted />;
  }

  return (
    <div className="dbs-studio-live-title">
      <GameSelectAll
        fetchData={fetchProjectData}
        onChange={handleStudioChange}
        onDataLoaded={handleStudioDataLoaded}
        defaultUnifiedId={gameTitle}
        urlParamKey="game_title"
        urlBasePath="/strategic/home"
        onBuildUrl={handleBuildUrl}
        urlUpdateCondition={urlUpdateCondition}
      />
      {studio?.unified_id === 'all' ? (
        <LiveVisualizationAll studio={studio} studioList={studioList} />
      ) : (
        /* @ts-ignore - LiveVisualization accepts studio prop but TypeScript doesn't recognize it */
        <LiveVisualization studio={studio} />
      )}
    </div>
  );
}
